import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const GiftButton = ({ practitioner }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [serviceId, setServiceId] = useState(practitioner.services?.[0]?.id || "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const service = practitioner.services?.find((s) => s.id === serviceId);
  if (user && user.id === practitioner.user_id) return null;

  const openDialog = () => {
    if (!user) {
      toast.info("Sign in to send a gift");
      navigate("/login");
      return;
    }
    setOpen(true);
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/checkout", {
        practitioner_id: practitioner.id, service_id: serviceId,
        recipient_name: name, recipient_email: email, message,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" data-testid="gift-session-btn" onClick={openDialog}
        className="w-full h-11 rounded-full mt-1">
        <Gift size={15} className="mr-2 text-primary" /> Gift this session
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="gift-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Gift a session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground -mt-2">
              You pay today — they pick whatever day and time feels right. We'll email them a beautiful redemption link.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Service</Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger data-testid="gift-service-select" className="mt-2"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                <SelectContent>
                  {practitioner.services?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} — ${s.price} CAD</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="gift-name" className="text-xs uppercase tracking-wider text-muted-foreground">Their name</Label>
                <Input id="gift-name" data-testid="gift-recipient-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="gift-email" className="text-xs uppercase tracking-wider text-muted-foreground">Their email</Label>
                <Input id="gift-email" data-testid="gift-recipient-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" />
              </div>
            </div>
            <div>
              <Label htmlFor="gift-message" className="text-xs uppercase tracking-wider text-muted-foreground">Personal message (optional)</Label>
              <Textarea id="gift-message" data-testid="gift-message-input" value={message} maxLength={500}
                onChange={(e) => setMessage(e.target.value)} placeholder="A little note to go with your gift…" className="mt-2 min-h-[80px]" />
            </div>
            <Button data-testid="gift-pay-btn" disabled={!serviceId || !name.trim() || !email.trim() || busy} onClick={pay}
              className="w-full h-11 rounded-full">
              {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <Gift size={15} className="mr-2" />}
              {busy ? "Redirecting to payment…" : service ? `Pay $${service.price} CAD & send gift` : "Pay & send gift"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">Secure payment via Stripe · The gift never expires</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
